import React, { useState } from "react"
import { Bus, Cpu, Network, LayoutDashboard, AppWindow, Plug, FileText, ChevronDown, ArrowRight, ExternalLink } from "lucide-react"
import { SectionHeading } from "./ui/SectionHeading"
import { PRODUCTS, Product, OFFER_PDF_URL, offerPdfPage } from "../data/products"

const ICONS: Record<Product["icon"], React.ReactNode> = {
	bus: <Bus size={22} />,
	cpu: <Cpu size={22} />,
	gateway: <Network size={22} />,
	layout: <LayoutDashboard size={22} />,
	app: <AppWindow size={22} />,
	plug: <Plug size={22} />
}

/**
 * Single product card with an expandable reference use case.
 */
const ProductCard: React.FC<{ product: Product; index: number }> = ({ product, index }) => {
	const [open, setOpen] = useState(false)
	const detailId = `product-${product.id}-usecase`

	return (
		<article
			className={`lp-card lp-card--bar lp-product${product.badge ? " lp-product--partner" : ""}`}
			data-reveal=""
			style={{ "--reveal-delay": `${(index % 3) * 100}ms` } as React.CSSProperties}
		>
			<div className="lp-product__head">
				<span className="lp-icon-badge" aria-hidden="true">{ICONS[product.icon]}</span>
				<div>
					<h3 className="lp-card__title">{product.name}</h3>
					<p className="lp-product__role">{product.role}</p>
				</div>
			</div>

			<div className="lp-product__tags">
				<span className="lp-tag">{product.tech}</span>
				{product.badge && <span className="lp-tag lp-tag--partner">{product.badge}</span>}
			</div>

			<p className="lp-card__text">{product.summary}</p>

			<ul className="lp-product__list">
				{product.highlights.map(item => (
					<li key={item}>{item}</li>
				))}
			</ul>

			<button
				type="button"
				className={`lp-product__toggle${open ? " is-open" : ""}`}
				onClick={() => setOpen(v => !v)}
				aria-expanded={open}
				aria-controls={detailId}
			>
				<ChevronDown size={16} /> {open ? "Skrýt případovou studii" : "Případová studie z praxe"}
			</button>

			{open && (
				<div className="lp-product__usecase" id={detailId}>
					<span className="lp-product__usecase-dept">{product.useCase.department}</span>
					<h4>{product.useCase.title}</h4>
					<p>{product.useCase.text}</p>
					<dl className="lp-product__metrics">
						{product.useCase.metrics.map(metric => (
							<div key={metric.label}>
								<dt>{metric.value}</dt>
								<dd>{metric.label}</dd>
							</div>
						))}
					</dl>
				</div>
			)}

			<div className="lp-product__links">
				<a href={offerPdfPage(product.pdfPage)} target="_blank" rel="noopener noreferrer">
					<FileText size={15} /> Popis v nabídce (PDF, str. {product.pdfPage})
				</a>
				{product.useCasePdfPage && (
					<a href={offerPdfPage(product.useCasePdfPage)} target="_blank" rel="noopener noreferrer">
						<ExternalLink size={15} /> Případová studie (str. {product.useCasePdfPage})
					</a>
				)}
			</div>
		</article>
	)
}

/**
 * Products Component — the automation ecosystem: our own products first, the Make partner adapter last.
 * Content mirrors the public offer document in /docs.
 */
export const Products: React.FC = () => {
	return (
		<section id="products" className="lp-section lp-section--soft lp-section--dots">
			<div className="lp-container">
				<SectionHeading
					eyebrow="Produkty"
					title="Automatizační ekosystém B2B"
					lead={
						<>
							Pět modulů, jeden plynulý datový tok – od příjmu dokumentu přes bezpečné zpracování až po distribuci
							do ERP, Excelu nebo webu. Nasazujete jen vrstvy, které potřebujete; každá sdílí stejné standardy
							pro stabilitu a ochranu dat. Kompletní popis, případové studie i ceník najdete v{" "}
							<a href={OFFER_PDF_URL} target="_blank" rel="noopener noreferrer">referenční nabídce (PDF)</a>.
						</>
					}
				/>

				<div className="lp-grid lp-grid--3">
					{PRODUCTS.map((product, index) => (
						<ProductCard key={product.id} product={product} index={index} />
					))}
				</div>

				<div className="lp-product__flow" data-reveal="">
					<span className="lp-eyebrow">Jak moduly spolupracují</span>
					<ol>
						<li><strong>Ingesce</strong> – Light B2B Gateway zachytí dokument z API, uploadu nebo sledované složky.</li>
						<li><strong>Směrování</strong> – GoRBus deterministicky předá událost správnému modulu.</li>
						<li><strong>Zpracování</strong> – Neuron Machine vytěží data lokální nebo cloudovou AI v izolovaném procesu.</li>
						<li><strong>Kontrola a distribuce</strong> – BrokerHFlow nechá člověka schválit výsledek; výstup míří do ERP, Excelu, Wordu nebo přes Make SDK do scénáře Make.com.</li>
					</ol>
				</div>

				<div className="lp-actions-center" data-reveal="">
					<a href={OFFER_PDF_URL} target="_blank" rel="noopener noreferrer" className="lp-btn lp-btn--primary">
						<FileText size={18} /> Stáhnout nabídku řešení (PDF)
					</a>
					<a href="#inquiry" className="lp-btn lp-btn--outline">
						Sestavit poptávku <ArrowRight size={16} />
					</a>
				</div>
			</div>
		</section>
	)
}
